import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { kategoriAdi, useKatalog, urunYolu } from "../data/katalog";
import { useKarsilastirma } from "../data/karsilastirma";
import { nitelikler } from "../data/urunNitelikleri";
import { markaYolu } from "../data/markalar";

// Ürün adından ek nitelikler (karşılaştırma tablosu için)
const motorTipi = (u) => (/kömürsüz/i.test(u.ad) || (u.ozellikler || []).some((o) => /kömürsüz/i.test(o)) ? "komursuz" : null);
const seri = (u) => (u.ad.match(/\b(XR|FlexVolt|XRP|PRO|Premium|Expert)\b/) || [])[1] || null;
const modelKodu = (u) => {
  const kod = (u.ozellikler || []).find((o) => /^Ürün kodu /.test(o));
  if (kod) return kod.replace(/^Ürün kodu /, "");
  return (u.ad.match(/\b([A-Z]{2,5}\d{2,5}[A-Z0-9]*)\b/) || [])[1] || null;
};

export default function Karsilastir() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const { urunler, adlar, hazir } = useKatalog();
  const { idler, cikar, temizle } = useKarsilastirma();
  const { ekle } = useCart();
  const [sadeceFark, setSadeceFark] = useState(false);

  useEffect(() => {
    document.title = `${t("compare.title")} — ${t("siteName")}`;
  }, [t]);

  const secilenler = useMemo(() => idler.map((id) => urunler.find((u) => u.id === id)).filter(Boolean), [idler, urunler]);

  // Satırlar: [anahtar, başlık, (ürün) => metin]
  const satirlar = useMemo(
    () => [
      ["marka", t("compare.rows.brand"), (u) => u.marka],
      ["kategori", t("compare.rows.category"), (u) => kategoriAdi(adlar, u.kategori, lang)],
      ["model", t("compare.rows.model"), (u) => modelKodu(u)],
      ["voltaj", t("shop.facets.voltaj"), (u) => nitelikler(u).voltaj.join(", ")],
      ["seri", t("compare.rows.series"), (u) => seri(u)],
      ["motor", t("compare.rows.motor"), (u) => (motorTipi(u) ? t("compare.brushless") : null)],
      ["aku", t("shop.facets.aku"), (u) => nitelikler(u).aku.map((a) => t(`shop.facets.aku_${a}`)).join(", ")],
      ["renk", t("shop.facets.renk"), (u) => nitelikler(u).renk.join(", ")],
      ["secenek", t("compare.rows.options"), (u) => (u.boyutlar || []).join(", ")],
      [
        "ozellik",
        t("compare.rows.features"),
        (u) =>
          (u.ozellikler || [])
            .filter((o) => !/^Ürün kodu /.test(o))
            .map((o) => t(`shop.productFeatures.${o}`, { defaultValue: "" }) || o)
            .join(" · "),
      ],
    ],
    [t, adlar, lang]
  );

  // Hiç veri olmayan satırlar gösterilmez; "sadece farklar" açıkken aynı olanlar da gizlenir
  const gorunenSatirlar = satirlar.filter(([, , f]) => {
    const degerler = secilenler.map((u) => f(u) || "");
    if (degerler.every((d) => !d)) return false;
    if (sadeceFark && new Set(degerler).size === 1) return false;
    return true;
  });

  if (hazir && secilenler.length === 0) {
    return (
      <section className="container-page section text-center">
        <h1 className="page-title">{t("compare.title")}</h1>
        <p className="mx-auto mt-4 max-w-md text-[15px] text-body">{t("compare.empty")}</p>
        <Link to="/urunler" className="btn-primary mt-8 inline-flex">
          {t("product.backToCatalog")}
        </Link>
      </section>
    );
  }

  return (
    <section className="container-page section">
      <div className="page-head flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t("compare.eyebrow")}</p>
          <h1 className="page-title mt-3">{t("compare.title")}</h1>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-[13.5px] text-body">
            <input type="checkbox" checked={sadeceFark} onChange={(e) => setSadeceFark(e.target.checked)} className="h-4 w-4 accent-accent" />
            {t("compare.onlyDiff")}
          </label>
          <button type="button" onClick={temizle} className="text-[13px] text-muted hover:text-accent">
            {t("compare.clear")}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-line">
        <table className="w-full min-w-[640px] border-collapse text-start text-[13.5px]">
          <thead>
            <tr>
              <th className="sticky start-0 z-10 w-40 bg-canvas p-4" />
              {secilenler.map((u) => (
                <th key={u.id} className="border-s border-line bg-white p-4 align-top font-normal">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => cikar(u.id)}
                      aria-label={t("compare.remove")}
                      className="absolute end-0 top-0 flex h-7 w-7 items-center justify-center rounded-full bg-canvas text-muted transition-colors hover:bg-ink hover:text-white"
                    >
                      ×
                    </button>
                    <Link to={urunYolu(u)} className="block">
                      <span className="relative mx-auto block aspect-square max-w-[160px]">
                        <img src={u.gorsel} alt={u.ad} className="absolute inset-0 h-full w-full object-contain p-2" />
                      </span>
                      <span className="mt-3 line-clamp-3 block text-[14px] font-semibold leading-snug text-ink hover:text-accent">{u.ad}</span>
                    </Link>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => ekle(u, (u.boyutlar || [])[0] || "")}
                        className="rounded-md border border-line px-3 py-1.5 text-[12.5px] font-medium text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white"
                      >
                        {t("shop.addToCart")}
                      </button>
                      {u.marka && (
                        <Link to={markaYolu(u.marka)} className="rounded-md px-2 py-1.5 text-[12.5px] text-muted hover:text-ink">
                          {u.marka}
                        </Link>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gorunenSatirlar.map(([anahtar, baslik, f]) => {
              const degerler = secilenler.map((u) => f(u) || "");
              const farkli = new Set(degerler).size > 1;
              return (
                <tr key={anahtar} className="border-t border-line">
                  <th scope="row" className="sticky start-0 z-10 bg-canvas p-4 text-start align-top text-[12px] font-semibold uppercase tracking-eyebrow text-muted">
                    {baslik}
                  </th>
                  {degerler.map((d, i) => (
                    <td key={i} className={`border-s border-line p-4 align-top leading-relaxed ${farkli ? "bg-accent/[0.04] text-ink" : "text-body"}`}>
                      {d || <span className="text-line">—</span>}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12px] text-muted">{t("compare.hint")}</p>
    </section>
  );
}
